// Secções que a Durius não tem (valores, pilares, galeria), desenhadas no mesmo estilo das restantes
import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Dumbbell, HeartHandshake, Leaf } from "lucide-react";
import { EKKO_DEFAULTS, ekkoList, ekkoText } from "@/lib/ekkoDefaults";
import { safeUrl } from "@/lib/whatsapp";

const PILLAR_ICONS = [Dumbbell, HeartHandshake, Leaf];

function SectionHeader({ content, prefix, isInView }) {
  const subtitle = ekkoText(content, `${prefix}_subtitle`);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="text-center mb-16"
    >
      <span className="block text-xs font-bold tracking-[0.18em] uppercase text-[#9A9A9A] mb-3">{ekkoText(content, `${prefix}_eyebrow`)}</span>
      <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] mb-4 tracking-wider rich-text-content" dangerouslySetInnerHTML={{ __html: ekkoText(content, `${prefix}_title`) }} />
      {subtitle && <p className="text-[#C9C9C9] text-base md:text-lg max-w-2xl mx-auto rich-text-content" dangerouslySetInnerHTML={{ __html: subtitle }} />}
    </motion.div>
  );
}

export function ValuesBand({ content = {} }) {
  const values = ekkoList(content, "values_items")
    .map((value, i) => (value || "").trim() || EKKO_DEFAULTS.values_items[i])
    .filter(Boolean);
  return (
    <div className="bg-[#A6C48A] py-5" data-testid="values-band">
      <ul className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center items-center gap-x-8 gap-y-2 text-[#010000] text-xs sm:text-sm font-bold uppercase tracking-[0.14em]">
        {values.map((value, i) => (
          <li key={value} className="flex items-center gap-8">
            {value}
            {i < values.length - 1 && <span className="w-1.5 h-1.5 rounded-full bg-[#010000]/50" aria-hidden="true" />}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PillarsSection({ content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const pillars = ekkoList(content, "pillars");
  return (
    <section id="pilares" ref={ref} className="py-24 md:py-32 bg-[#010000] relative" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader content={content} prefix="pillars" isInView={isInView} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((pillar, i) => {
            const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
            const tags = (pillar.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
            return (
              <motion.article
                key={`${pillar.title}-${i}`}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                className="bg-[#111111] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#A6C48A] hover:-translate-y-1 transition-all duration-300"
              >
                <span className="inline-flex w-14 h-14 rounded-xl bg-[#A6C48A]/15 items-center justify-center text-[#A6C48A] mb-6">
                  <Icon size={26} />
                </span>
                <h3 className="text-2xl font-bold text-[#F5F5F5] tracking-wider mb-3 uppercase">{pillar.title}</h3>
                <p className="text-[#C9C9C9] mb-6">{pillar.text}</p>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-5 border-t border-[#2A2A2A]">
                    {tags.map((tag) => (
                      <span key={tag} className="text-xs font-bold tracking-wide px-3 py-1 rounded-full bg-[#010000] border border-[#2A2A2A] text-[#F5F5F5]">{tag}</span>
                    ))}
                  </div>
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
      <div className="section-divider mt-24" />
    </section>
  );
}

export function galleryImages(content) {
  return ekkoList(content, "gallery_images").filter((img) => safeUrl(img?.image));
}

export function GallerySection({ content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <section id="espaco" ref={ref} className="py-24 md:py-32 bg-[#111111] relative" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader content={content} prefix="gallery" isInView={isInView} />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {galleryImages(content).map((img, i) => (
            <motion.figure
              key={`${img.image}-${i}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.08 * i }}
              className="overflow-hidden rounded-2xl bg-[#1F1F1F] group"
            >
              <img src={img.image} alt={img.alt || ""} loading="lazy" className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700" />
            </motion.figure>
          ))}
        </div>
      </div>
      <div className="section-divider mt-24" />
    </section>
  );
}

export function testimonialsList(content) {
  return (content.testimonials || []).filter((t) => (t?.text || "").trim() && (t?.name || "").trim());
}

export function TestimonialsSection({ content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <section id="testemunhos" ref={ref} className="py-24 md:py-32 bg-[#010000] relative" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader content={content} prefix="testimonials" isInView={isInView} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonialsList(content).map((t, i) => (
            <motion.figure
              key={`${t.name}-${i}`}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.08 * i }}
              className="bg-[#111111] border border-[#2A2A2A] rounded-2xl p-8 flex flex-col"
            >
              <span className="text-5xl leading-none text-[#A6C48A] font-bold mb-2" aria-hidden="true">“</span>
              <blockquote className="text-[#F5F5F5] text-lg flex-1">{t.text}</blockquote>
              <figcaption className="mt-6 pt-5 border-t border-[#2A2A2A]">
                <span className="block font-bold text-[#F5F5F5]">{t.name}</span>
                {t.detail && <span className="block text-sm text-[#9A9A9A]">{t.detail}</span>}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
      <div className="section-divider mt-24" />
    </section>
  );
}

export function faqList(content) {
  return (content.faq || []).filter((f) => (f?.question || "").trim() && (f?.answer || "").trim());
}

export function FaqSection({ content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <section id="perguntas" ref={ref} className="py-24 md:py-32 bg-[#111111] relative" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader content={content} prefix="faq" isInView={isInView} />
        <div className="space-y-3">
          {faqList(content).map((f, i) => (
            <details key={`${f.question}-${i}`} className="group rounded-2xl border border-[#2A2A2A] bg-[#010000] open:border-[#A6C48A]">
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-6 py-5 font-bold text-[#F5F5F5] text-lg">
                {f.question}
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#A6C48A]/40 flex items-center justify-center text-xl transition-transform group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              <p className="px-6 pb-6 text-[#C9C9C9] whitespace-pre-line">{f.answer}</p>
            </details>
          ))}
        </div>
      </div>
      <div className="section-divider mt-24" />
    </section>
  );
}
